import type { ReactNode } from 'react'

/**
 * A segment that spans columns within a single week. Generic over T so
 * callers can attach domain-specific payload that the renderer consumes.
 */
export interface WeekBarSegment<T = unknown> {
  id: string
  /** 0-indexed start column (inclusive). For Mon-first weeks, Mon=0 .. Sun=6. */
  startCol: number
  /** 0-indexed end column (inclusive). Equal to startCol for single-day. */
  endCol: number
  /** Caller-defined payload — rendered by the bar renderer. */
  data: T
}

export interface WeekBarOverlayProps<T = unknown> {
  /** Segments to pack and render. Will be sorted internally. */
  bars: ReadonlyArray<WeekBarSegment<T>>
  /** Total columns in the week. Default 7. */
  cols?: number
  /** Maximum bar rows before overflowing. Default 3. */
  maxRows?: number
  /**
   * Renders one bar's content. Receives the segment's data + position hints
   * (whether the bar starts/ends at the week boundary).
   *
   * For single-week use both are always true. The flags are included so the
   * API is forward-compatible with multi-week wrappers that clip segments at
   * week boundaries and need square vs. rounded left/right ends.
   */
  renderBar: (args: {
    data: T
    startsHere: boolean
    endsHere: boolean
  }) => ReactNode
  /**
   * Optional overflow renderer. Receives the count of segments that didn't
   * fit in the visible rows. Default renders a "+N more" badge.
   */
  renderOverflow?: (overflowCount: number) => ReactNode
  /**
   * Optional fixed-row bands rendered ABOVE the packed bar rows (e.g.,
   * blackout windows). They don't participate in row packing. Each band gets
   * its own row, in order.
   */
  bands?: ReadonlyArray<{
    id: string
    startCol: number
    endCol: number
    render: ReactNode
  }>
  /** Bar height per row in px. Default 20. */
  barHeight?: number
  /** Vertical gap between rows in px. Default 4. */
  rowGap?: number
  /**
   * Horizontal inset (px) applied to each bar/band on BOTH sides, so bars sit
   * inside their day span with a gap and never touch the cell gridlines.
   * Default 0.
   */
  barGap?: number
  'data-testid'?: string
  /**
   * When true, the per-row "+N more" chip and its 20px row are not rendered.
   * Use when the consumer wants to render its own per-cell overflow indicator.
   */
  suppressOverflowRow?: boolean
}

function defaultOverflow(count: number): ReactNode {
  return (
    <span
      style={{
        fontFamily: 'var(--font-mono, inherit)',
        fontSize: 10,
        color: 'var(--surface)',
        padding: '2px 6px',
        background: 'var(--border-strong)',
        borderRadius: 4,
        display: 'inline-block',
      }}
    >
      +{count} more
    </span>
  )
}

/**
 * WeekBarOverlay — reusable presentation layer for week-spanning bar charts.
 *
 * Accepts generic segments with column positions, packs them into rows with a
 * greedy first-fit algorithm, and renders them as absolutely-positioned bars
 * over an implicit 7-column (or `cols`-column) grid. Bands render as fixed
 * rows above the packed rows. Domain logic lives entirely in the `renderBar`
 * and `bands[].render` slots.
 */
export function WeekBarOverlay<T = unknown>({
  bars,
  cols = 7,
  maxRows = 3,
  renderBar,
  renderOverflow = defaultOverflow,
  bands = [],
  barHeight = 20,
  rowGap = 4,
  barGap = 0,
  'data-testid': testId,
  suppressOverflowRow = false,
}: WeekBarOverlayProps<T>): ReactNode {
  // Greedy first-fit row packing — sort by startCol first.
  const sorted = [...bars].sort((a, b) => a.startCol - b.startCol)
  const rows: Array<Array<WeekBarSegment<T>>> = []

  for (const seg of sorted) {
    let placed = false
    for (const row of rows) {
      const last = row[row.length - 1]!
      if (last.endCol < seg.startCol) {
        row.push(seg)
        placed = true
        break
      }
    }
    if (!placed) rows.push([seg])
  }

  const visibleRows = rows.slice(0, maxRows)
  // Overflow count = number of segments in rows beyond maxRows.
  const overflowCount = rows
    .slice(maxRows)
    .reduce((sum, row) => sum + row.length, 0)

  return (
    <div
      data-testid={testId}
      style={{
        display: 'flex',
        flexDirection: 'column',
        gap: rowGap,
      }}
    >
      {/* Fixed-height band rows rendered above packed bars */}
      {bands.map((band) => {
        const span = band.endCol - band.startCol + 1
        const left = (band.startCol / cols) * 100
        const width = (span / cols) * 100
        return (
          <div
            key={band.id}
            style={{ position: 'relative', height: barHeight }}
          >
            <div
              style={{
                position: 'absolute',
                left: `calc(${left}% + ${barGap}px)`,
                width: `calc(${width}% - ${barGap * 2}px)`,
                top: 0,
                bottom: 0,
              }}
            >
              {band.render}
            </div>
          </div>
        )
      })}

      {/* Packed bar rows */}
      {visibleRows.map((row, rowIndex) => (
        <div
          key={rowIndex}
          style={{ position: 'relative', height: barHeight }}
        >
          {row.map((seg) => {
            const span = seg.endCol - seg.startCol + 1
            const left = (seg.startCol / cols) * 100
            const width = (span / cols) * 100
            return (
              <div
                key={seg.id}
                style={{
                  position: 'absolute',
                  left: `calc(${left}% + ${barGap}px)`,
                  width: `calc(${width}% - ${barGap * 2}px)`,
                  top: 0,
                  bottom: 0,
                }}
              >
                {renderBar({
                  data: seg.data,
                  // Single-week use: both flags are always true.
                  // A multi-week wrapper clips segments at week boundaries and
                  // should pass startsHere/endsHere from the clipped segment.
                  startsHere: true,
                  endsHere: true,
                })}
              </div>
            )
          })}
        </div>
      ))}

      {/* Overflow row — suppressed when the consumer renders per-cell chips instead */}
      {!suppressOverflowRow && overflowCount > 0 ? (
        <div style={{ height: barHeight, display: 'flex', alignItems: 'center' }}>
          {renderOverflow(overflowCount)}
        </div>
      ) : null}
    </div>
  )
}
